import { useCallback, useEffect, useRef, useState } from 'react';
import { formatMYR } from '@72on/domain';
import {
  AdminError,
  isUnauthorized,
  approveDevice,
  createPairingCodeFor,
  revokeDevice,
  getSessionInfo,
  listDevices,
  logout,
  changeEmail,
  changePassword,
  renameTenant,
  updateProfile,
  setTaxEnabled as saveTaxSetting,
  setReceiptBranding,
  setVariantCosts,
  exportCatalogCsv,
  setTrialStart,
  updateVariant,
  type DeviceRow,
  createProduct,
  importCatalogCsv,
  listProducts,
  type ImportError,
  type ImportPreview,
  type Product,
} from '../offline/admin.js';
import { createStaff, disableStaff, listStaff, resetStaffPin, type StaffRow } from '../offline/staff.js';
import type { PosStore } from '../offline/store.js';
import { agentHealth, openDrawer, printTest, type AgentHealth } from '../offline/agent.js';
import { SalesPanel } from './SalesPanel.js';
import { ReportsPanel } from './ReportsPanel.js';
import { LoginForm } from './LoginForm.js';
import {
  clearAdminSession,
  readAdminSession,
  readLastIdentity,
  saveAdminSession,
} from '../offline/admin-session.js';

/** Kunci automatik back office selepas 10 minit tanpa sentuhan. */
const IDLE_LOCK_MS = 10 * 60 * 1000;

import { API_BASE, BUILD_VERSION } from '../config.js';

interface Props {
  store: PosStore;
  onDone: () => void;
}

/**
 * Back office minimum: senarai produk, tambah produk, dan pengimport CSV.
 *
 * Sebab ia wujud: tanpa ini, katalog hanya boleh diisi melalui `curl`. Kedai tidak boleh mula
 * tanpa cara memasukkan barangnya sendiri.
 */
export function AdminScreen({ store, onDone }: Props): React.ReactElement {
  const [token, setToken] = useState<string | undefined>(undefined);
  const [needsLogin, setNeedsLogin] = useState(false);
  /** Butiran log masuk terakhir untuk diisi semula, supaya pemilik tidak menaip e-mel setiap kali. */
  const [prefill, setPrefill] = useState<{ email: string; slug: string } | null>(null);
  /**
   * Skrin muat semula: sesi dan e-mel dibaca sebelum borang dipaparkan.
   *
   * Tanpa ini, borang log masuk dirender dengan medan kosong, dan `useState(initialEmail)` tidak
   * dikemas kini selepas nilai itu tiba — jadi pemilik melihat kotak kosong walaupun e-melnya
   * disimpan.
   */
  const [booting, setBooting] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [barcode, setBarcode] = useState('');
  const [unit, setUnit] = useState('pcs');

  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [csvErrors, setCsvErrors] = useState<ImportError[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const [agentTokenInput, setAgentTokenInput] = useState('');
  /** Token ejen yang tersimpan dipaparkan sebagai teks biasa apabila diminta. */
  const [showAgentToken, setShowAgentToken] = useState(false);
  const [agentStatus, setAgentStatus] = useState<AgentHealth | null>(null);
  const [agentMessage, setAgentMessage] = useState<string | null>(null);

  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [staffName, setStaffName] = useState('');
  const [staffPin, setStaffPin] = useState('');
  const [staffMessage, setStaffMessage] = useState<string | null>(null);
  const [staffBusy, setStaffBusy] = useState(false);
  const [outlets, setOutlets] = useState<Array<{ id: string; name: string; code: string }>>([]);
  // Nama kedai dipaparkan dalam header: nama peranti tidak unik merentas kedai, jadi "Kaunter 1"
  // tidak boleh dikenal pasti tanpa nama kedai di sebelahnya.
  const [shopName, setShopName] = useState('');
  const [selectedOutlet, setSelectedOutlet] = useState('');

  // ---- Panel Tetapan ----
  const [accountRole, setAccountRole] = useState('');
  const [profileName, setProfileName] = useState('');
  const [shopNameInput, setShopNameInput] = useState('');
  const [emailNew, setEmailNew] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [receiptHeader, setReceiptHeader] = useState('');
  /**
   * Tetapan sesi sudah dimuatkan?
   *
   * Borang dirender serta-merta, tetapi nilai sesi tiba kemudian. Jika pemilik menaip dalam tempoh
   * itu, `loadWorkspace` yang lewat menimpa tulisan mereka — dan "Simpan" kemudian melihat medan
   * kosong. Itu punca laporan "kepala resit tidak kekal".
   */
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  /** Maklum balas blok resit — dipaparkan DI BAWAH medan, bukan di hujung panel. */
  const [receiptMessage, setReceiptMessage] = useState<{ text: string; error: boolean } | null>(null);
  /** Pengguna sudah menyunting kepala/footer: jangan timpa dengan nilai pelayan. */
  const brandingDirty = useRef(false);
  const [receiptFooter, setReceiptFooter] = useState('');
  /** Kos per unit semasa menambah produk baharu. */
  const [cost, setCost] = useState('');
  /** Penapis: tunjuk hanya produk yang belum mempunyai kos. */
  const [onlyWithoutCost, setOnlyWithoutCost] = useState(false);
  /** Draf kos: variantId → teks yang ditaip pemilik (RM). */
  const [costDraft, setCostDraft] = useState<Record<string, string>>({});
  const [costBusy, setCostBusy] = useState(false);
  const [costMessage, setCostMessage] = useState<{ text: string; error: boolean } | null>(null);
  /** Tarikh mula percubaan 30 hari (BRIEF §8). */
  const [trialStart, setTrialStartState] = useState('');
  /** Varian sedang disunting. */
  /** Maklum balas dalam modal: kegagalan mesti kelihatan DI TEMPAT pengguna melihat. */
  const [editingMessage, setEditingMessage] = useState<{ text: string; error: boolean } | null>(null);
  /**
   * Maklum balas panel Tambah produk.
   *
   * Ia dipaparkan DI DALAM panel, bukan sebagai toast di atas halaman: pengguna sedang melihat
   * borang itu, dan mesej di tempat lain kelihatan seperti "tiada apa-apa berlaku".
   */
  const [addMessage, setAddMessage] = useState<{ text: string; error: boolean } | null>(null);
  const [editing, setEditing] = useState<{
    id: string;
    name: string;
    price: string;
    cost: string;
    barcode: string;
  } | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [pairingCode, setPairingCode] = useState<{
    code: string;
    device_no: number;
    expires_at: string;
  } | null>(null);
  const [deviceMessage, setDeviceMessage] = useState<string | null>(null);

  const load = useCallback(
    async (search: string, authToken: string) => {
      try {
        setProducts(await listProducts(API_BASE, authToken, search));
      } catch (error) {
        if (isUnauthorized(error)) {
          clearAdminSession();
          setNeedsLogin(true);
          return;
        }
        setMessage(error instanceof Error ? error.message : 'gagal memuatkan produk');
      }
    },
    [],
  );

  /**
   * Muatkan semua data sesi: produk, cawangan, dan peranti.
   *
   * SATU fungsi, dipanggil semasa halaman dibuka DAN selepas log masuk. Versi sebelumnya memuatkan
   * cawangan/peranti hanya pada pembukaan halaman, jadi selepas log masuk baharu senarai kekal
   * kosong dan "Jana kod pairing" berkata "Tiada cawangan aktif".
   */
  const loadWorkspace = useCallback(
    async (authToken: string) => {
      await load('', authToken);
      try {
        const session = await getSessionInfo(API_BASE, authToken);
        setOutlets(session.outlets);
        setShopName(session.tenant.name ?? '');
        // Panel Tetapan bermula dengan nilai semasa, bukan kotak kosong.
        setAccountRole(session.role);
        setProfileName(session.user.name);
        setShopNameInput(session.tenant.name ?? '');
        setTaxEnabled(session.settings.tax_enabled === true);
        if (!brandingDirty.current) {
          setReceiptHeader((session.settings.receipt_header ?? []).join('\n'));
          setReceiptFooter(session.settings.receipt_footer ?? '');
        }
        setTrialStartState(session.settings.trial_started_on ?? '');
        setSelectedOutlet((current) => (current === '' ? (session.outlets[0]?.id ?? '') : current));
        setDevices(await listDevices(API_BASE, authToken));
        setStaff(await listStaff(API_BASE, authToken));
        setDeviceMessage(null);
      } catch (error) {
        if (isUnauthorized(error)) {
          setNeedsLogin(true);
          return;
        }
        // Jangan telan ralat: senarai kosong mesti bermakna "tiada peranti", bukan "permintaan
        // gagal". Perbezaan itu menentukan siapa yang perlu bertindak.
        setDeviceMessage(error instanceof Error ? error.message : 'gagal memuatkan senarai peranti');
      } finally {
        setSettingsLoaded(true);
      }
    },
    [load, store],
  );

  useEffect(() => {
    void (async () => {
      /*
       * Sesi tab: F5 tidak memaksa log masuk semula, tetapi membuka admin DARIPADA POS sentiasa
       * berkunci, dan 12 jam had keras.
       */
      /*
       * Sesi tab: F5 kekal dalam back office. Sesi hanya wujud apabila pemilik log masuk di tempat
       * ini, dan ia dipadam setiap kali keluar ke POS.
       */
      const saved = readAdminSession();
      setPrefill(saved !== null ? { email: saved.email, slug: saved.slug } : readLastIdentity());

      if (saved !== null) {
        setToken(saved.token);
        await loadWorkspace(saved.token);
      }

      /*
       * Tanpa sesi, back office bermula terkunci.
       *
       * Sebelum ini token tersimpan dimuatkan terus, jadi sesiapa yang menggunakan PC kaunter —
       * termasuk kasir yang log masuk dengan PIN — boleh menekan "Admin" dan melihat laporan,
       * tetapan dan senarai peranti tanpa kata laluan. POS ialah terminal berkongsi; back office
       * mesti meminta kata laluan setiap kali dibuka.
       *
       * Token pemilik kekal dalam storan kerana POS memerlukannya untuk menyegarkan katalog. Ia
       * hanya boleh digunakan selepas kunci dibuka.
       */
      if (saved === null) {
        setToken(undefined);
        setNeedsLogin(false);
      }

      // Skrin boleh dipaparkan sekarang; status ejen cetak tidak sepatutnya menahan UI.
      setBooting(false);

      // Ejen cetak: papar status walaupun token sudah disimpan sebelum ini.
      const printerToken = await store.getAgentToken();
      if (printerToken !== undefined) {
        setAgentTokenInput(printerToken);
        setAgentStatus(await agentHealth(printerToken));
      }
    })();
  }, [store, loadWorkspace]);

  /**
   * Kunci automatik.
   *
   * Skrin back office yang dibiarkan terbuka pada PC kaunter ialah cara paling mudah maklumat
   * kedai terdedah kepada pelanggan seterusnya. Selepas 10 minit tanpa sentuhan, ia berkunci semula.
   */
  useEffect(() => {
    if (token === undefined) return;

    let timer = window.setTimeout(() => {
      clearAdminSession();
      setToken(undefined);
    }, IDLE_LOCK_MS);
    const reset = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        clearAdminSession();
        setToken(undefined);
      }, IDLE_LOCK_MS);
    };

    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel'];
    for (const event of events) window.addEventListener(event, reset, { passive: true });

    return () => {
      window.clearTimeout(timer);
      for (const event of events) window.removeEventListener(event, reset);
    };
  }, [token]);

  /** Jana kod pairing untuk peranti tambahan (kaunter kedua). */
  async function addDevice(): Promise<void> {
    if (token === undefined) return;

    // Cawangan diambil daripada SESI, bukan daripada identiti peranti. Tablet yang hanya membuka
    // laporan tidak mempunyai peranti, jadi identiti peranti tidak boleh menjadi sumber cawangan —
    // itulah punca mesej "Peranti ini belum dipasangkan" pada tablet.
    // Cawangan daripada senarai yang sudah dimuatkan; jika belum tiba (klik sejurus selepas log
    // masuk), ambil terus daripada sesi. Butang tidak boleh bergantung pada data yang belum sampai.
    let outletId = selectedOutlet !== '' ? selectedOutlet : (outlets[0]?.id ?? '');
    if (outletId === '') {
      try {
        const session = await getSessionInfo(API_BASE, token);
        setOutlets(session.outlets);
        outletId = session.outlets[0]?.id ?? '';
        setSelectedOutlet(outletId);
      } catch {
        // Abaikan: mesej di bawah menjelaskan keadaannya.
      }
    }
    if (outletId === '') {
      outletId = (await store.getIdentity())?.outletId ?? '';
    }

    if (outletId === undefined || outletId === '') {
      setDeviceMessage('Tiada cawangan aktif untuk sesi ini');
      return;
    }

    const deviceNo = Math.max(0, ...devices.map((device) => device.device_no)) + 1;
    if (deviceNo > 99) {
      setDeviceMessage('Had 99 peranti dicapai');
      return;
    }

    setDeviceMessage(null);
    try {
      const result = await createPairingCodeFor(API_BASE, token, {
        outletId,
        name: deviceName.trim() === '' ? `Kaunter ${deviceNo}` : deviceName.trim(),
      });
      setPairingCode(result);
      setDeviceName('');
      setDevices(await listDevices(API_BASE, token));
    } catch (error) {
      setDeviceMessage(error instanceof Error ? error.message : 'gagal menjana kod');
    }
  }

  /**
  /**
   * Luluskan peranti yang menunggu.
   *
   * Semakan manusia yang menggantikan kata laluan pemilik pada skrin persediaan: pengurus melihat
   * nama, IP dan peranti, kemudian membenarkan ia menerima wang.
   */
  /**
   * Staf dan PIN.
   *
   * PIN ialah kredensial lemah, jadi ia hanya untuk kasir dan hanya pada peranti yang dipasangkan.
   * Pemilik dan pengurus menggunakan kata laluan.
   */
  async function addStaff(): Promise<void> {
    if (token === undefined) return;
    setStaffBusy(true);
    setStaffMessage(null);
    try {
      await createStaff(API_BASE, token, { name: staffName.trim(), pin: staffPin });
      setStaff(await listStaff(API_BASE, token));
      setStaffMessage(`Kasir ${staffName.trim()} ditambah. Beritahu dia PIN-nya secara terus.`);
      setStaffName('');
      setStaffPin('');
    } catch (error) {
      setStaffMessage(error instanceof Error ? error.message : 'gagal menambah kasir');
    } finally {
      setStaffBusy(false);
    }
  }

  async function resetPinFor(row: StaffRow): Promise<void> {
    if (token === undefined) return;
    const pin = window.prompt(`PIN baharu untuk ${row.name} (4-6 digit):`);
    if (pin === null || pin.trim() === '') return;

    setStaffBusy(true);
    setStaffMessage(null);
    try {
      await resetStaffPin(API_BASE, token, row.user_id, pin.trim());
      setStaff(await listStaff(API_BASE, token));
      setStaffMessage(`PIN ${row.name} ditetapkan semula, dan kunci (jika ada) dibuka.`);
    } catch (error) {
      setStaffMessage(error instanceof Error ? error.message : 'gagal menetapkan PIN');
    } finally {
      setStaffBusy(false);
    }
  }

  async function removeStaff(row: StaffRow): Promise<void> {
    if (token === undefined) return;
    if (
      !window.confirm(
        `Nyahaktifkan ${row.name}? Dia tidak lagi boleh log masuk, tetapi jualan lamanya kekal dalam rekod.`,
      )
    ) {
      return;
    }

    setStaffBusy(true);
    setStaffMessage(null);
    try {
      await disableStaff(API_BASE, token, row.user_id);
      setStaff(await listStaff(API_BASE, token));
      setStaffMessage(`${row.name} dinyahaktifkan.`);
    } catch (error) {
      setStaffMessage(error instanceof Error ? error.message : 'gagal menyahaktifkan kasir');
    } finally {
      setStaffBusy(false);
    }
  }

  /**
   * Tetapan akaun dan perniagaan.
   *
   * Semua tindakan di sini mengubah perkara yang menjejaskan log masuk atau notifikasi, jadi setiap
   * satu melaporkan hasilnya secara jelas — dan kegagalan tidak disembunyikan.
   */
  async function saveProfile(): Promise<void> {
    if (token === undefined) return;
    setSettingsBusy(true);
    setSettingsMessage(null);
    try {
      await updateProfile(API_BASE, token, profileName.trim());
      setSettingsMessage('Nama disimpan.');
    } catch (error) {
      setSettingsMessage(error instanceof Error ? error.message : 'gagal menyimpan nama');
    } finally {
      setSettingsBusy(false);
    }
  }

  /**
   * Hidupkan/matikan cukai jualan.
   *
   * Lalai mati. Perubahan hanya menjejaskan jualan BAHARU — resit yang sudah dicetak tidak boleh
   * berubah, dan cukai yang sudah dikutip mesti kekal dalam rekod.
   */
  /**
   * Simpan kepala dan footer resit.
   *
   * Ia dihantar bersama setiap kerja cetakan, jadi ia berkuat kuasa pada resit seterusnya tanpa
   * menyunting fail pada setiap PC.
   */
  async function saveReceiptBranding(): Promise<void> {
    if (token === undefined) return;
    setSettingsBusy(true);
    setSettingsMessage(null);

    // JANGAN potong secara senyap. Baris kelima yang hilang tanpa mesej ialah pepijat yang
    // dilaporkan: pemilik menambah "TEL:", menyimpan, dan nombor itu tidak pernah sampai.
    const lines = receiptHeader
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '');

    if (lines.length === 0) {
      setReceiptMessage({ text: 'Kepala resit tidak boleh kosong.', error: true });
      setSettingsBusy(false);
      return;
    }
    if (lines.length > 6) {
      setReceiptMessage({
        text: `Kepala resit ada ${lines.length} baris; maksimum 6 baris.`,
        error: true,
      });
      setSettingsBusy(false);
      return;
    }
    const longLine = lines.find((line) => line.length > 60);
    if (longLine !== undefined) {
      setReceiptMessage({
        text: `Baris "${longLine.slice(0, 20)}…" ialah ${longLine.length} aksara; maksimum 60.`,
        error: true,
      });
      setSettingsBusy(false);
      return;
    }

    try {
      await setReceiptBranding(API_BASE, token, lines, receiptFooter.trim());
      brandingDirty.current = false;
      setReceiptHeader(lines.join('\n'));
      setReceiptMessage({
        text: 'Kepala dan footer resit disimpan. Ia akan dicetak pada resit seterusnya.',
        error: false,
      });
    } catch (error) {
      setReceiptMessage({
        text: error instanceof Error ? error.message : 'Gagal menyimpan resit.',
        error: true,
      });
    } finally {
      setSettingsBusy(false);
    }
  }

  /** Simpan suntingan varian: nama, harga, kos, barcode. */
  /**
   * Simpan semua kos yang diisi dalam satu permintaan.
   *
   * Hanya baris yang diisi dihantar: kosong bermakna "belum tahu", bukan sifar. Baris yang tidak sah
   * dilaporkan mengikut nama produk supaya pemilik boleh membetulkannya tanpa meneka.
   */
  async function saveCosts(): Promise<void> {
    if (token === undefined) return;

    const items: Array<{ variantId: string; costSen: number }> = [];
    const invalid: string[] = [];

    for (const [variantId, raw] of Object.entries(costDraft)) {
      if (raw.trim() === '') continue;
      const sen = Math.round(Number(raw.replace(',', '.')) * 100);
      if (!Number.isFinite(sen) || sen < 0) {
        invalid.push(variantId);
        continue;
      }
      items.push({ variantId, costSen: sen });
    }

    if (invalid.length > 0) {
      setCostMessage({ text: `${invalid.length} kos tidak sah. Guna nombor, contoh 12.50.`, error: true });
      return;
    }
    if (items.length === 0) {
      setCostMessage({ text: 'Isi sekurang-kurangnya satu kos.', error: true });
      return;
    }

    setCostBusy(true);
    setCostMessage(null);
    try {
      const result = await setVariantCosts(API_BASE, token, items);
      setCostDraft({});
      // Toast global: panel boleh hilang selepas refresh (semua kos sudah diisi), jadi mesej di
      // dalam panel tidak dapat dilihat. Pengguna mesti tetap tahu ia berjaya.
      if (result.missing.length > 0) {
        // Kejayaan separa: beritahu dengan jelas, jangan sembunyikan.
        setMessage(`${result.updated.length} kos disimpan, ${result.missing.length} varian tidak dijumpai.`);
        setCostMessage({
          text: `${result.missing.length} varian tidak dijumpai (mungkin dipadam). Muat semula halaman.`,
          error: true,
        });
      } else {
        setMessage(`${result.updated.length} kos disimpan.`);
        setCostMessage(null);
      }
      await refresh();
    } catch (error) {
      setCostMessage({
        text: error instanceof Error ? error.message : 'Gagal menyimpan kos.',
        error: true,
      });
    } finally {
      setCostBusy(false);
    }
  }

  /** Tetapkan tarikh mula percubaan 30 hari. */
  /** Muat turun katalog sebagai CSV (sandaran, atau pindah ke kedai baharu). */
  async function downloadCatalog(): Promise<void> {
    if (token === undefined) return;
    setBusy(true);
    try {
      const csv = await exportCatalogCsv(API_BASE, token);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'katalog-72on.csv';
      link.click();
      URL.revokeObjectURL(url);
      setMessage('Katalog dieksport.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'gagal mengeksport katalog');
    } finally {
      setBusy(false);
    }
  }

  async function saveTrialStart(): Promise<void> {
    if (token === undefined) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trialStart)) {
      setSettingsMessage('Pilih tarikh mula percubaan.');
      return;
    }
    setSettingsBusy(true);
    setSettingsMessage(null);
    try {
      const saved = await setTrialStart(API_BASE, token, trialStart);
      setTrialStartState(saved);
      setSettingsMessage(`Percubaan bermula ${saved}. Angka §8 dikira dari tarikh ini.`);
    } catch (error) {
      setSettingsMessage(error instanceof Error ? error.message : 'gagal menyimpan tarikh');
    } finally {
      setSettingsBusy(false);
    }
  }

  async function saveVariant(): Promise<void> {
    // Sebelum ini fungsi ini kembali secara SENYAP apabila token tiada, dan ralat hanya
    // dilaporkan sebagai mesej di bahagian atas halaman — di luar pandangan pengguna yang sedang
    // melihat modal. Butang kelihatan "tidak melakukan apa-apa".
    if (editing === null) return;
    if (token === undefined) {
      setEditingMessage({ text: 'Sesi tamat. Log masuk semula, kemudian cuba lagi.', error: true });
      return;
    }

    const priceSen = Math.round(Number(editing.price.replace(',', '.')) * 100);
    const costSen =
      editing.cost.trim() === ''
        ? 0
        : Math.round(Number(editing.cost.replace(',', '.')) * 100);

    if (!Number.isFinite(priceSen) || priceSen <= 0) {
      setEditingMessage({ text: 'Harga mesti nombor lebih daripada sifar.', error: true });
      return;
    }
    if (!Number.isFinite(costSen) || costSen < 0) {
      setEditingMessage({ text: 'Kos mesti nombor sifar atau lebih.', error: true });
      return;
    }

    setEditingMessage({ text: 'Menyimpan…', error: false });
    setBusy(true);
    try {
      await updateVariant(API_BASE, token, editing.id, {
        name: editing.name.trim() === '' ? null : editing.name.trim(),
        price_sen: priceSen,
        cost_sen: costSen,
        barcode: editing.barcode.trim() === '' ? null : editing.barcode.trim(),
      });
      setEditing(null);
      setEditingMessage(null);
      setMessage('Produk dikemas kini');
      await refresh();
    } catch (error) {
      setEditingMessage({
        text: error instanceof Error ? error.message : 'Gagal mengemas kini produk.',
        error: true,
      });
    } finally {
      setBusy(false);
    }
  }

  async function saveTax(): Promise<void> {
    if (token === undefined) return;
    setSettingsBusy(true);
    setSettingsMessage(null);
    try {
      const result = await saveTaxSetting(API_BASE, token, taxEnabled);
      setTaxEnabled(result.tax_enabled);
      setSettingsMessage(
        result.tax_enabled
          ? 'Cukai dihidupkan. Jualan baharu akan mengenakan cukai bagi produk yang mempunyai kadar cukai. Tekan "Segarkan katalog" pada POS.'
          : 'Cukai dimatikan. Jualan baharu tidak akan mengenakan cukai. Tekan "Segarkan katalog" pada POS.',
      );
    } catch (error) {
      setSettingsMessage(error instanceof Error ? error.message : 'gagal menyimpan tetapan cukai');
    } finally {
      setSettingsBusy(false);
    }
  }

  async function saveShopName(): Promise<void> {
    if (token === undefined) return;
    setSettingsBusy(true);
    setSettingsMessage(null);
    try {
      await renameTenant(API_BASE, token, shopNameInput.trim());
      setShopName(shopNameInput.trim());
      setSettingsMessage('Nama perniagaan disimpan. Ia muncul di back office dan dalam e-mel.');
    } catch (error) {
      setSettingsMessage(error instanceof Error ? error.message : 'gagal menyimpan nama perniagaan');
    } finally {
      setSettingsBusy(false);
    }
  }

  async function saveEmail(): Promise<void> {
    if (token === undefined) return;
    setSettingsBusy(true);
    setSettingsMessage(null);
    try {
      const result = await changeEmail(API_BASE, token, emailPassword, emailNew.trim());
      setSettingsMessage(
        `E-mel ditukar kepada ${result.email}. Notifikasi kini pergi ke alamat itu. Sila log masuk semula dengan alamat baharu.`,
      );
      setEmailNew('');
      setEmailPassword('');
    } catch (error) {
      setSettingsMessage(error instanceof Error ? error.message : 'gagal menukar e-mel');
    } finally {
      setSettingsBusy(false);
    }
  }

  async function savePassword(): Promise<void> {
    if (token === undefined) return;
    if (pwNew !== pwConfirm) {
      setSettingsMessage('Kata laluan baharu dan pengesahannya tidak sepadan.');
      return;
    }
    setSettingsBusy(true);
    setSettingsMessage(null);
    try {
      await changePassword(API_BASE, token, pwCurrent, pwNew);
      setSettingsMessage('Kata laluan ditukar. Semua sesi lain telah dilog keluar.');
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (error) {
      setSettingsMessage(error instanceof Error ? error.message : 'gagal menukar kata laluan');
    } finally {
      setSettingsBusy(false);
    }
  }

  /**
   * Jana kod untuk MENGGANTIKAN peranti yang dicabut.
   *
   * PC yang sama mesti mengekalkan nombor perantinya: jika ia mendapat #18, resit baharu akan
   * bercampur dengan kaunter lain dalam laporan dan Z-report.
   *
   * Kod ini juga memberi kunci baharu — dan kunci itu tidak boleh dieksport (CryptoKey), jadi ia
   * menutup kelemahan kunci lama yang boleh disalin keluar dari PC.
   */
  async function replaceDevice(device: DeviceRow): Promise<void> {
    if (token === undefined) return;
    setDeviceMessage(null);
    try {
      const result = await createPairingCodeFor(API_BASE, token, {
        outletId: selectedOutlet === '' ? (outlets[0]?.id ?? '') : selectedOutlet,
        name: device.name,
        deviceNo: device.device_no,
      });
      setPairingCode(result);
      setDeviceMessage(
        `Kod untuk menggantikan peranti #${device.device_no}. Pada PC itu: buka POS → "Pasang semula peranti" → masukkan kod ini → kemudian luluskan di sini.`,
      );
      setDevices(await listDevices(API_BASE, token));
    } catch (error) {
      setDeviceMessage(error instanceof Error ? error.message : 'gagal menjana kod gantian');
    }
  }

  async function approve(device: DeviceRow): Promise<void> {
    if (token === undefined) return;
    try {
      await approveDevice(API_BASE, token, device.id);
      setDevices(await listDevices(API_BASE, token));
      setDeviceMessage(`Peranti #${device.device_no} diluluskan.`);
    } catch (error) {
      setDeviceMessage(error instanceof Error ? error.message : 'gagal meluluskan peranti');
    }
  }

  /**
   * Cabut peranti selepas pengesahan.
   *
   * Pengesahan menyebut akibatnya secara jelas: PC itu berhenti menghantar jualan, dan jualan yang
   * belum dihantar akan hilang. Kasir mesti sync dahulu.
   */
  async function removeDevice(device: DeviceRow): Promise<void> {
    if (token === undefined) return;
    const confirmed = window.confirm(
      `Cabut peranti #${device.device_no} "${device.name}"?\n\n` +
        'PC itu tidak lagi boleh menghantar jualan.\n\n' +
        'SEBELUM mencabut: lihat bar atas POS pada PC itu. Jika ia menyebut "N belum dihantar", ' +
        'tekan Sync sehingga ia berkata "disinkronkan". Jualan yang belum dihantar hanya wujud ' +
        'dalam PC itu — ia hilang jika PC dibuang atau storan pelayarnya dipadam.\n\n' +
        'Resit dan laporan lama tidak dipadam.',
    );
    if (!confirmed) return;

    try {
      const result = await revokeDevice(API_BASE, token, device.id);
      setDevices(await listDevices(API_BASE, token));
      setDeviceMessage(
        `Peranti #${result.device_no} dicabut. Jualan yang belum dihantar pada PC itu hanya boleh dihantar selepas ia dipasangkan semula — jangan buang PC itu atau padam storan pelayarnya.`,
      );
    } catch (error) {
      setDeviceMessage(error instanceof Error ? error.message : 'gagal mencabut peranti');
    }
  }
  async function saveAgentToken(): Promise<void> {
    const value = agentTokenInput.trim();
    if (value === '') {
      setAgentMessage('Token tidak boleh kosong');
      return;
    }
    await store.saveAgentToken(value);
    const health = await agentHealth(value);
    setAgentStatus(health);

    if (!health.available) {
      setAgentMessage(
        'Ejen tidak menjawab pada 127.0.0.1:7321. Tetingkap ejen (start.cmd) berjalan?',
      );
      return;
    }

    // Ejen berjalan tetapi origin berbeza: pelayar menyekat setiap permintaan cetak, dan tanpanya
    // gejala kelihatan seperti "printer tidak berfungsi". Beritahu tepat apa yang perlu diubah.
    const here = window.location.origin;
    if (health.allowedOrigin !== null && health.allowedOrigin !== here) {
      setAgentMessage(
        `Ejen berjalan, tetapi melarang origin ${here}. Dalam agent.json tetapkan "allowedOrigin": "${here}", kemudian mula semula start.cmd.`,
      );
      return;
    }

    setAgentMessage(
      health.printerConnected
        ? `Ejen OK · printer ${health.printerName ?? '(tanpa nama)'} · ${health.paperWidthMm ?? '?'} mm`
        : 'Ejen OK, tetapi printer belum dikonfigurasi (masih mod fail). Tetapkan "transport" dalam agent.json.',
    );
  }

  async function testPrint(): Promise<void> {
    const value = (await store.getAgentToken()) ?? agentTokenInput.trim();
    if (value === '') {
      setAgentMessage('Simpan token ejen dahulu');
      return;
    }
    const outcome = await printTest(value);
    setAgentMessage(
      outcome.printed
        ? 'Halaman ujian dihantar ke printer'
        : outcome.queued
          ? `Dibariskan: ${outcome.error ?? 'printer belum sedia'}`
          : `Gagal: ${outcome.error ?? '(tiada sebab)'}`,
    );
  }

  async function testDrawer(): Promise<void> {
    const value = (await store.getAgentToken()) ?? agentTokenInput.trim();
    if (value === '') {
      setAgentMessage('Simpan token ejen dahulu');
      return;
    }
    const opened = await openDrawer(value, `drawer-admin-${Date.now()}`);
    setAgentMessage(opened ? 'Perintah buka laci dihantar' : 'Laci tidak dapat dibuka');
  }

  /**
   * Log keluar: batalkan sesi pada pelayan, buang token tempatan, dan kembali ke borang log masuk.
   *
   * Identiti peranti tidak disentuh — kasir terus berjual. Yang hilang hanya akses pemilik.
   */
  async function signOut(): Promise<void> {
    const current = token;
    if (current !== undefined) await logout(API_BASE, current);
    clearAdminSession();
    setToken(undefined);
    setNeedsLogin(false);
    setDevices([]);
    setPairingCode(null);
    setMessage(null);
  }
  async function refresh(): Promise<void> {
    if (token !== undefined) await load(query, token);
  }

  async function addProduct(): Promise<void> {
    if (token === undefined) return;
    const priceSen = Math.round(Number(price.replace(',', '.')) * 100);
    if (name.trim() === '' || !Number.isFinite(priceSen) || priceSen <= 0) {
      setAddMessage({ text: 'Nama dan harga diperlukan.', error: true });
      return;
    }

    // Kos pilihan: kosong bermakna belum diisi, bukan sifar yang disengajakan.
    const costSen =
      cost.trim() === '' ? undefined : Math.round(Number(cost.replace(',', '.')) * 100);
    if (costSen !== undefined && (!Number.isFinite(costSen) || costSen < 0)) {
      setAddMessage({ text: 'Kos mesti nombor sifar atau lebih.', error: true });
      return;
    }

    setBusy(true);
    try {
      await createProduct(API_BASE, token, {
        name: name.trim(),
        variants: [
          {
            unit,
            price_sen: priceSen,
            // Kos yang diisi dalam borang mesti sampai ke pelayan. Tanpa baris ini, medan kos
            // kelihatan berfungsi tetapi nilainya dibuang.
            ...(costSen === undefined ? {} : { cost_sen: costSen }),
            ...(barcode.trim() === '' ? {} : { barcode: barcode.trim() }),
          },
        ],
      });
      setName('');
      setPrice('');
      setCost('');
      setBarcode('');
      setAddMessage({ text: 'Produk ditambah.', error: false });
      await refresh();
    } catch (error) {
      setAddMessage({
        text:
          error instanceof AdminError && error.code === 'BARCODE_TAKEN'
            ? 'Barcode itu sudah digunakan oleh produk lain.'
            : error instanceof Error
              ? error.message
              : 'Gagal menambah produk.',
        error: true,
      });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Muat fail CSV yang dipilih atau dijatuhkan.
   *
   * Sebelum ini borang hanya menerima teks yang ditampal, jadi pemilik yang mempunyai fail CSV di
   * cakera melihat butang Import kekal lumpuh — tiada cara memilih fail.
   */
  async function loadCsvFile(file: File): Promise<void> {
    const text = await file.text();
    setCsv(text);
    setFileName(file.name);
    setPreview(null);
    setCsvErrors([]);

    if (text.includes('\uFFFD')) {
      setMessage(
        'Fail ini mungkin bukan UTF-8 — aksara Melayu mungkin rosak. Simpan semula sebagai "CSV UTF-8" dalam Excel.',
      );
    }

    // Pratonton automatik: pemilik perlu melihat bilangan produk sebelum menekan Import.
    await runImport('preview', text);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file !== undefined) void loadCsvFile(file);
  }

  async function runImport(mode: 'preview' | 'commit', override?: string): Promise<void> {
    if (token === undefined) return;
    const payload = override ?? csv;
    setBusy(true);
    setCsvErrors([]);
    try {
      const result = await importCatalogCsv(API_BASE, token, payload, mode);
      if (result.mode === 'preview') {
        setPreview(result);
        setMessage(null);
      } else {
        setPreview(null);
        setCsv('');
        setFileName(null);
        setMessage(
          `Import selesai: ${result.productsCreated} produk, ${result.variantsCreated} varian, ${result.stockApplied} dengan stok awal`,
        );
        await refresh();
      }
    } catch (error) {
      if (error instanceof AdminError && error.code === 'CSV_INVALID') {
        const details = error.details as { errors?: ImportError[] } | undefined;
        setCsvErrors(details?.errors ?? []);
        setMessage(error.message);
      } else {
        setMessage(error instanceof Error ? error.message : 'import gagal');
      }
    } finally {
      setBusy(false);
    }
  }

  if (booting) {
    return (
      <div className="setup">
        <p className="muted">Memuatkan back office…</p>
      </div>
    );
  }

  if (token === undefined || needsLogin) {
    return (
      <LoginForm
        reason={
          needsLogin
            ? 'Back office berkunci. Masukkan kata laluan untuk membuka.'
            : 'Back office memerlukan sesi pemilik.'
        }
        onCancel={onDone}
        initialEmail={prefill?.email}
        initialSlug={prefill?.slug}
        onToken={(next, meta) => {
          saveAdminSession({ token: next, email: meta.email, slug: meta.slug });
          setPrefill({ email: meta.email, slug: meta.slug });
          setToken(next);
          setNeedsLogin(false);
          void loadWorkspace(next);
        }}
      />
    );
  }

  return (
    <div className="admin">
      <header className="bar">
        <strong>72on Back Office</strong>
        {shopName !== '' && <span className="shop-name">{shopName}</span>}
        <span className="muted build">{BUILD_VERSION}</span>
        <button
          type="button"
          className="ghost"
          onClick={() => {
            // Keluar ke POS = keluar dari back office. Kasir tidak mewarisi sesi ini.
            clearAdminSession();
            onDone();
          }}
        >
          Kembali ke POS
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => {
            /*
             * Kunci = muat semula halaman.
             *
             * Sesi ditandakan berkunci dahulu, kemudian halaman dimuat semula. Ini memastikan
             * SEMUA state dalam memori (produk, peranti, laporan, token) benar-benar hilang —
             * bukan sekadar disembunyikan oleh satu keadaan React.
             */
            clearAdminSession();
            window.location.reload();
          }}
        >
          Kunci
        </button>
        <button type="button" className="ghost" onClick={() => void signOut()}>
          Log keluar
        </button>
      </header>

      {message !== null && (
        <div className="toast" onClick={() => setMessage(null)}>
          {message}
        </div>
      )}

      <main className="admin-body">
        <section className="panel-printer">
          <h2>Ejen cetak</h2>
          <p className="muted">
            Ejen mesti berjalan pada PC ini. Token dipaparkan pada tetingkap ejen setiap kali ia
            bermula; jika hilang, jalankan <code>node agent.mjs --token</code> dalam folder ejen, atau
            buka <code>%LOCALAPPDATA%\72on-agent\agent.json</code>.
          </p>
          <div className="printer-row">
            <label>
              Token ejen
              <input
                type={showAgentToken ? 'text' : 'password'}
                value={agentTokenInput}
                onChange={(event) => setAgentTokenInput(event.target.value)}
                spellCheck={false}
                placeholder="32 aksara"
              />
            </label>
            <button
              type="button"
              className="link"
              onClick={() => setShowAgentToken((current) => !current)}
            >
              {showAgentToken ? 'Sembunyikan token' : 'Tunjuk token'}
            </button>
            <button type="button" onClick={() => void saveAgentToken()}>
              Simpan &amp; semak
            </button>
            <button type="button" className="ghost" onClick={() => void testPrint()}>
              Cetak halaman ujian
            </button>
            <button type="button" className="ghost" onClick={() => void testDrawer()}>
              Buka laci
            </button>
          </div>
          {agentStatus !== null && (
            <p className="muted">
              {agentStatus.available
                ? `Ejen: OK · printer ${agentStatus.printerConnected ? `bersambung (${agentStatus.printerName ?? 'tanpa nama'})` : 'BELUM bersambung'} · ${agentStatus.paperWidthMm ?? '?'} mm`
                : 'Ejen: tidak menjawab'}
            </p>
          )}
          {agentMessage !== null && <p className="muted">{agentMessage}</p>}
        </section>

        <section className="panel-settings">
          <h2>Tetapan</h2>
          <p className="muted">
            Perubahan di sini menjejaskan log masuk dan notifikasi. Hanya anda yang boleh
            melakukannya — tiada siapa di 72on boleh menukar kata laluan anda.
          </p>

          <div className="settings-grid">
            <div className="settings-block">
              <h3>Nama anda</h3>
              <label>
                Nama paparan
                <input
                  value={profileName}
                  onChange={(event) => setProfileName(event.target.value)}
                  maxLength={120}
                />
              </label>
              <button
                type="button"
                onClick={() => void saveProfile()}
                disabled={settingsBusy || profileName.trim() === ''}
              >
                Simpan nama
              </button>
            </div>

            <div className="settings-block">
              <h3>Nama perniagaan</h3>
              <label>
                Nama perniagaan
                <input
                  value={shopNameInput}
                  onChange={(event) => setShopNameInput(event.target.value)}
                  maxLength={120}
                />
              </label>
              <button
                type="button"
                onClick={() => void saveShopName()}
                disabled={settingsBusy || accountRole !== 'owner' || shopNameInput.trim() === ''}
              >
                Simpan nama perniagaan
              </button>
              {accountRole !== 'owner' && (
                <p className="muted">Hanya pemilik boleh menukar nama perniagaan.</p>
              )}
            </div>

            <div className="settings-block">
              <h3>Cukai jualan (SST)</h3>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={taxEnabled}
                  onChange={(event) => setTaxEnabled(event.target.checked)}
                  disabled={accountRole !== 'owner'}
                />
                Kenakan cukai kepada pelanggan
              </label>
              <p className="muted">
                Lalai <strong>mati</strong>. Kebanyakan kedai kecil tidak mengenakan cukai, dan cukai
                yang terlepas pandang menaikkan harga pelanggan tanpa sebab.
              </p>
              <p className="muted">
                Hanya menjejaskan jualan <strong>baharu</strong>. Produk mesti mempunyai kadar cukai
                (SST 5% atau 10%) untuk cukai dikenakan. Selepas menukar, tekan{' '}
                <strong>Segarkan katalog</strong> pada POS.
              </p>
              <button
                type="button"
                onClick={() => void saveTax()}
                disabled={settingsBusy || accountRole !== 'owner'}
              >
                Simpan tetapan cukai
              </button>
              {accountRole !== 'owner' && (
                <p className="muted">Hanya pemilik boleh menukar tetapan cukai.</p>
              )}
            </div>

            <div className="settings-block">
              <h3>Kepala dan footer resit</h3>
              <label>
                Kepala resit (satu baris setiap satu, maksimum 4)
                <textarea
                  disabled={!settingsLoaded}
                  value={receiptHeader}
                  onChange={(event) => {
                    brandingDirty.current = true;
                    setReceiptHeader(event.target.value);
                  }}
                  rows={6}
                  maxLength={360}
                  placeholder={'NAMA KEDAI\nAlamat\n07-123 4567'}
                />
              </label>
              <label>
                Footer resit
                <input
                  disabled={!settingsLoaded}
                  value={receiptFooter}
                  onChange={(event) => {
                    brandingDirty.current = true;
                    setReceiptFooter(event.target.value);
                  }}
                  maxLength={300}
                  placeholder="Terima kasih. Barang yang dibeli tidak boleh dipulangkan."
                />
              </label>
              <button
                type="button"
                onClick={() => void saveReceiptBranding()}
                disabled={settingsBusy || accountRole !== 'owner' || !settingsLoaded}
              >
                Simpan resit
              </button>
              {receiptMessage !== null && (
                <p className={receiptMessage.error ? 'error' : 'muted'}>{receiptMessage.text}</p>
              )}
              <p className="muted">
                Maksimum 6 baris, 60 aksara setiap baris. Baris yang lebih panjang dibalut oleh
                ejen; kertas 58 mm muat ~32 aksara setiap baris, 80 mm ~46.
              </p>
            </div>

            <div className="settings-block">
              <h3>Percubaan 30 hari</h3>
              <label>
                Tarikh mula
                <input
                  type="date"
                  value={trialStart}
                  onChange={(event) => setTrialStartState(event.target.value)}
                />
              </label>
              <button type="button" disabled={settingsBusy} onClick={() => void saveTrialStart()}>
                Mula percubaan
              </button>
              <p className="muted">
                Angka §8 (beza tunai, lag sync, masa tutup syif, liputan kos) dikira daripada tarikh
                ini di panel Laporan.
              </p>
            </div>

            <div className="settings-block">
              <h3>E-mel log masuk</h3>
              <label>
                E-mel baharu
                <input
                  type="email"
                  value={emailNew}
                  onChange={(event) => setEmailNew(event.target.value)}
                  spellCheck={false}
                  placeholder="nama@kedai.com"
                />
              </label>
              <label>
                Kata laluan semasa
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(event) => setEmailPassword(event.target.value)}
                />
              </label>
              <button
                type="button"
                onClick={() => void saveEmail()}
                disabled={settingsBusy || emailNew.trim() === '' || emailPassword === ''}
              >
                Tukar e-mel
              </button>
              <p className="muted">
                Notifikasi kelulusan peranti akan pergi ke alamat baharu. Alamat lama dimaklumkan
                tentang perubahan ini.
              </p>
            </div>

            <div className="settings-block">
              <h3>Kata laluan</h3>
              <label>
                Kata laluan semasa
                <input
                  type="password"
                  value={pwCurrent}
                  onChange={(event) => setPwCurrent(event.target.value)}
                />
              </label>
              <label>
                Kata laluan baharu (minimum 10 aksara)
                <input
                  type="password"
                  value={pwNew}
                  onChange={(event) => setPwNew(event.target.value)}
                />
              </label>
              <label>
                Ulang kata laluan baharu
                <input
                  type="password"
                  value={pwConfirm}
                  onChange={(event) => setPwConfirm(event.target.value)}
                />
              </label>
              <button
                type="button"
                onClick={() => void savePassword()}
                disabled={settingsBusy || pwCurrent === '' || pwNew.length < 10}
              >
                Tukar kata laluan
              </button>
              <p className="muted">
                Semua sesi lain (PC, tablet, telefon) akan dilog keluar serta-merta.
              </p>
            </div>
          </div>

          {settingsMessage !== null && <p className="muted">{settingsMessage}</p>}
        </section>

        <section className="panel-staff">
          <h2>Staf</h2>
          <p className="muted">
            Kasir log masuk dengan PIN pada PC kaunter. Setiap jualan kemudian dikaitkan dengan nama
            mereka — jadi anda boleh tahu siapa menjual apa. Pemilik dan pengurus tidak menggunakan
            PIN; mereka menggunakan kata laluan.
          </p>

          <div className="printer-row">
            <label>
              Nama kasir
              <input
                value={staffName}
                onChange={(event) => setStaffName(event.target.value)}
                maxLength={120}
                placeholder="cth. Ali"
              />
            </label>
            <label>
              PIN (4–6 digit)
              <input
                value={staffPin}
                onChange={(event) => setStaffPin(event.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                maxLength={6}
                placeholder="cth. 481920"
              />
            </label>
            <button
              type="button"
              onClick={() => void addStaff()}
              disabled={staffBusy || staffName.trim() === '' || staffPin.length < 4}
            >
              Tambah kasir
            </button>
          </div>

          <p className="muted">
            Elakkan PIN seperti 0000, 1111, 1234, atau 4321 — ia ditolak kerana terlalu mudah
            diteka.
          </p>

          {staffMessage !== null && <p className="muted">{staffMessage}</p>}

          <table className="table">
            <thead>
              <tr>
                <th>Nama</th>
                <th>Peranan</th>
                <th>PIN</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {staff.map((row) => (
                <tr key={row.user_id}>
                  <td>{row.name}</td>
                  <td>{row.role}</td>
                  <td>
                    {row.has_pin
                      ? row.locked_until !== null
                        ? 'dikunci sementara'
                        : 'ditetapkan'
                      : 'tiada'}
                  </td>
                  <td>{row.status}</td>
                  <td>
                    {row.role !== 'owner' && (
                      <>
                        <button
                          type="button"
                          className="link"
                          onClick={() => void resetPinFor(row)}
                          disabled={staffBusy}
                        >
                          Tetapkan PIN
                        </button>
                        {row.status === 'active' && (
                          <button
                            type="button"
                            className="link"
                            onClick={() => void removeStaff(row)}
                            disabled={staffBusy}
                          >
                            Nyahaktif
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Belum ada kasir. Tambah yang pertama di atas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="panel-devices">
          <h2>Peranti</h2>
          <p className="muted">
            Setiap PC kaunter memerlukan kod pairing sendiri. Kod sah 15 minit dan hanya boleh
            digunakan sekali.
          </p>
          <div className="printer-row">
            <label>
              Nama peranti (pilihan)
              <input
                value={deviceName}
                onChange={(event) => setDeviceName(event.target.value)}
                placeholder={`Kaunter ${Math.max(0, ...devices.map((device) => device.device_no)) + 1}`}
              />
            </label>
            {outlets.length > 1 && (
              <label>
                Cawangan
                <select
                  value={selectedOutlet}
                  onChange={(event) => setSelectedOutlet(event.target.value)}
                >
                  {outlets.map((outlet) => (
                    <option key={outlet.id} value={outlet.id}>
                      {outlet.code} — {outlet.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="button" onClick={() => void addDevice()}>
              Jana kod pairing
            </button>
          </div>

          {pairingCode !== null && (
            <div className="pairing-code">
              <p className="muted">
                Buka <strong>app.72on.com</strong> pada PC baharu, kemudian masukkan kod ini pada
                skrin “Sediakan peranti”:
              </p>
              <p className="code">{pairingCode.code}</p>
              <p className="muted">
                Peranti #{pairingCode.device_no} — nombor resitnya akan bermula{' '}
                {pairingCode.device_no.toString().padStart(2, '0')}.
              </p>
              <p className="muted">
                Sah sehingga{' '}
                {new Date(pairingCode.expires_at).toLocaleTimeString('ms-MY', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                . Jika tamat, jana kod baharu.
              </p>
            </div>
          )}

          {deviceMessage !== null && <p className="error">{deviceMessage}</p>}

          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nama</th>
                <th>Cawangan</th>
                <th>Status</th>
                <th>Terakhir dilihat</th>
                <th>IP / peranti</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.some((device) => device.key_protection === 'exportable' && device.status !== 'revoked') && (
                <p className="muted">
                  Sesetengah peranti menggunakan <strong>kunci boleh dieksport</strong> (kunci lama).
                  Kunci itu boleh disalin keluar dari PC dengan DevTools. Pasangkan semula peranti itu
                  untuk mendapatkan kunci yang tidak boleh dieksport.
                </p>
              )}
              {devices.map((device) => (
                <tr key={device.id}>
                  <td>{device.device_no}</td>
                  <td>{device.name}</td>
                  <td>{device.outlet_code}</td>
                  <td>
                    {device.status === 'revoked'
                      ? 'dicabut'
                      : device.approved
                        ? 'aktif'
                        : device.paired
                          ? 'menunggu kelulusan'
                          : 'menunggu kod'}
                  </td>
                  <td>
                    {device.last_seen_at === null
                      ? '—'
                      : new Date(device.last_seen_at).toLocaleString('ms-MY')}
                  </td>
                  <td className="ip">
                    {device.pairing_ip ?? '—'}
                    {device.pairing_user_agent !== null && (
                      <span className="ua">{device.pairing_user_agent.slice(0, 42)}</span>
                    )}
                  </td>
                  <td>
                    {device.status !== 'revoked' && !device.approved && device.paired && (
                      <button type="button" className="link" onClick={() => void approve(device)}>
                        Luluskan
                      </button>
                    )}
                    {device.status === 'active' && (
                      <button
                        type="button"
                        className="link"
                        onClick={() => void removeDevice(device)}
                      >
                        Cabut
                      </button>
                    )}
                    {device.status === 'revoked' && (
                      <button
                        type="button"
                        className="link"
                        onClick={() => void replaceDevice(device)}
                      >
                        Ganti peranti
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {devices.length === 0 && <p className="muted">Tiada peranti lagi.</p>}
        </section>

        <section className="panel-products">
          <h2>Produk ({products.length})</h2>
          <input
            className="search"
            placeholder="Cari produk…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              if (token !== undefined) void load(event.target.value, token);
            }}
          />
                    {products.some((product) => product.variants.some((variant) => variant.cost_sen === 0)) && (
            <div className="notice">
              <p>
                <strong>
                  {products.flatMap((product) => product.variants).filter((variant) => variant.cost_sen === 0).length}
                </strong>{' '}
                varian belum mempunyai kos. Untung dalam laporan <strong>tidak dikira</strong> untuk varian
                ini — jualan mereka kelihatan 100% untung.
              </p>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={onlyWithoutCost}
                  onChange={(event) => setOnlyWithoutCost(event.target.checked)}
                />
                Tunjuk hanya varian tanpa kos
              </label>
            </div>
          )}

          <table className="table table-products">
            <thead>
              <tr>
                <th>Nama</th>
                <th>Varian</th>
                <th>Barcode</th>
                <th>Harga</th>
                <th>Kos</th>
                <th>Tindakan</th>
              </tr>
            </thead>
            <tbody>
              {products
                .flatMap((product) =>
                  product.variants.map((variant, index) => ({ product, variant, index })),
                )
                .filter(({ variant }) => !onlyWithoutCost || variant.cost_sen === 0)
                .map(({ product, variant, index }) => (
                  <tr key={variant.id}>
                    <td>{index === 0 ? product.name : ''}</td>
                    <td>{variant.name ?? '—'}</td>
                    <td>{variant.barcode ?? '—'}</td>
                    <td>RM{formatMYR(variant.price_sen)}</td>
                    <td>
                      {variant.cost_sen > 0 ? (
                        `RM${formatMYR(variant.cost_sen)}`
                      ) : (
                        <span className="muted">belum diisi</span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          setEditingMessage(null);
                          setEditing({
                            id: variant.id,
                            name: variant.name ?? '',
                            price: (variant.price_sen / 100).toFixed(2),
                            cost: variant.cost_sen > 0 ? (variant.cost_sen / 100).toFixed(2) : '',
                            barcode: variant.barcode ?? '',
                          });
                        }}
                      >
                        Sunting
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {products.length === 0 && <p className="muted">Belum ada produk.</p>}

          {editing !== null && (
            <div className="modal">
              <div className="sheet">
                <h3>Sunting varian</h3>
                <label>
                  Nama varian
                  <input
                    value={editing.name}
                    onChange={(event) =>
                      setEditing({ ...editing, name: event.target.value })
                    }
                  />
                </label>
                <label>
                  Harga (RM)
                  <input
                    inputMode="decimal"
                    value={editing.price}
                    onChange={(event) =>
                      setEditing({ ...editing, price: event.target.value })
                    }
                  />
                </label>
                <label>
                  Kos (RM)
                  <input
                    inputMode="decimal"
                    value={editing.cost}
                    onChange={(event) =>
                      setEditing({ ...editing, cost: event.target.value })
                    }
                    placeholder="Harga belian per unit"
                  />
                </label>
                <label>
                  Barcode
                  <input
                    value={editing.barcode}
                    onChange={(event) =>
                      setEditing({ ...editing, barcode: event.target.value })
                    }
                  />
                </label>
                <p className="muted">
                  Kos hanya menjejaskan jualan <strong>baharu</strong>. Jualan yang sudah direkod kekal
                  dengan kos pada masa jualan itu.
                </p>
                {editingMessage !== null && (
                  <p className={editingMessage.error ? 'error' : 'muted'}>{editingMessage.text}</p>
                )}
                <div className="row">
                  <button type="button" disabled={busy} onClick={() => void saveVariant()}>
                    {busy ? 'Menyimpan…' : 'Simpan'}
                  </button>
                  <button type="button" className="ghost" onClick={() => setEditing(null)}>
                    Batal
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {products.some((product) => product.variants.some((variant) => variant.cost_sen === 0)) && (
          <section className="panel-costs">
            <h2>Kos belum diisi</h2>
            <p className="muted">
              Isi harga belian per unit. Kos tidak dihantar ke POS; ia hanya digunakan untuk mengira
              untung. Kosongkan jika belum tahu — jangan tulis 0.
            </p>
            {costMessage !== null && (
              <p className={costMessage.error ? 'error' : 'muted'}>{costMessage.text}</p>
            )}
            <table className="table table-costs">
              <thead>
                <tr>
                  <th>Produk</th>
                  <th>Varian</th>
                  <th>Harga</th>
                  <th>Kos (RM)</th>
                </tr>
              </thead>
              <tbody>
                {products
                  .flatMap((product) =>
                    product.variants
                      .filter((variant) => variant.cost_sen === 0)
                      .map((variant) => ({ product, variant })),
                  )
                  .map(({ product, variant }) => (
                    <tr key={variant.id}>
                      <td>{product.name}</td>
                      <td>{variant.name ?? '—'}</td>
                      <td>RM{formatMYR(variant.price_sen)}</td>
                      <td>
                        <input
                          inputMode="decimal"
                          placeholder="0.00"
                          value={costDraft[variant.id] ?? ''}
                          onChange={(event) =>
                            setCostDraft((current) => ({ ...current, [variant.id]: event.target.value }))
                          }
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <button type="button" disabled={costBusy} onClick={() => void saveCosts()}>
              {costBusy ? 'Menyimpan…' : 'Simpan semua kos'}
            </button>
          </section>
        )}

        <section className="panel-add">
          <h2>Tambah produk</h2>
          <button type="button" className="ghost" disabled={busy} onClick={() => void downloadCatalog()}>
            Eksport katalog (CSV)
          </button>
          {addMessage !== null && (
            <p className={addMessage.error ? 'error' : 'muted'}>{addMessage.text}</p>
          )}
          <label>
            Nama
            <input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>
            Harga (RM)
            <input inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} />
          </label>
          <label>
            Kos (RM, pilihan)
            <input
              inputMode="decimal"
              value={cost}
              onChange={(event) => setCost(event.target.value)}
              placeholder="Harga belian per unit"
            />
          </label>
          <label>
            Barcode (pilihan)
            <input value={barcode} onChange={(event) => setBarcode(event.target.value)} />
          </label>
          <label>
            Unit
            <select value={unit} onChange={(event) => setUnit(event.target.value)}>
              <option value="pcs">pcs</option>
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="l">l</option>
              <option value="ml">ml</option>
              <option value="m">m</option>
            </select>
          </label>
          <button type="button" disabled={busy} onClick={() => void addProduct()}>
            Tambah
          </button>
        </section>

        <section className="panel-import">
          <h2>Import CSV</h2>
          <p className="muted">
            Lajur: <code>nama, varian, sku, barcode, harga, kos, unit, stok, cukai</code>. Baris dengan
            nama sama menjadi satu produk dengan beberapa varian.
          </p>

          <div
            className={dragging ? 'dropzone dragging' : 'dropzone'}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <label className="file-pick">
              <span className="file-pick-title">Pilih fail CSV</span>
              <span className="file-pick-hint">atau lepaskan fail ke sini</span>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) void loadCsvFile(file);
                  event.target.value = '';
                }}
              />
            </label>
            {fileName !== null && (
              <p className="muted">Fail dimuatkan: <strong>{fileName}</strong></p>
            )}
          </div>

          <p className="muted">Atau tampal kandungan CSV di sini:</p>
          <textarea
            className="csv"
            value={csv}
            onChange={(event) => {
              setCsv(event.target.value);
              setPreview(null);
              setFileName(null);
            }}
            placeholder={'nama,varian,harga,unit,stok\nPam Akuarium,500L,89.00,pcs,10\nIkan Guppy,,45.00,kg,2.5'}
          />

          <div className="actions">
            <button
              type="button"
              className="ghost"
              disabled={busy || csv.trim() === ''}
              onClick={() => void runImport('preview')}
            >
              Pratonton
            </button>
            <button
              type="button"
              disabled={busy || preview === null}
              onClick={() => void runImport('commit')}
            >
              {busy ? 'Memproses…' : 'Import'}
            </button>
          </div>

          {/* Butang yang lumpuh tanpa sebab ialah teka-teki. Beritahu langkah seterusnya. */}
          <p className="muted">
            {csv.trim() === ''
              ? 'Pilih fail atau tampal CSV untuk mula.'
              : preview === null
                ? 'Tekan Pratonton untuk mengesahkan, kemudian Import akan aktif.'
                : `Sedia diimport: ${preview.summary.products} produk, ${preview.summary.variants} varian. Tekan Import.`}
          </p>

          {preview !== null && (
            <ul className="import-summary">
              <li>{preview.summary.products} produk</li>
              <li>{preview.summary.variants} varian</li>
              <li>{preview.summary.withStock} dengan stok awal</li>
              {preview.summary.warnings > 0 && <li>{preview.summary.warnings} amaran</li>}
            </ul>
          )}

          {csvErrors.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Baris</th>
                  <th>Medan</th>
                  <th>Masalah</th>
                </tr>
              </thead>
              <tbody>
                {csvErrors.map((error) => (
                  <tr key={`${error.row}-${error.field}`}>
                    <td>{error.row}</td>
                    <td>{error.field}</td>
                    <td>{error.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <SalesPanel
          token={token}
          store={store}
          receiptHeader={receiptHeader}
          receiptFooter={receiptFooter}
        />

        <ReportsPanel token={token} trialStart={trialStart} />
      </main>
    </div>
  );
}
